<template>
  <default-textual-section v-if="$store.state.displayType === 'textual'" :menu="menu" :section="section"
                           :menu-item-component="(menuItemComponent.length) ? menuItemComponent : 'romanza-default-item'"
                           is-title-buffer-visible
                           :menu-item-tag-location="menuItemTagLocation"
                           class="menu--section-romanza"></default-textual-section>
  <default-visual-section v-else :menu="menu" :section="section"
                          :menu-item-component="(menuItemComponent.length) ? menuItemComponent : 'romanza-default-item'"
                          is-title-buffer-visible
                          :menu-item-tag-location="menuItemTagLocation"
                          class="menu--section-romanza"></default-visual-section>
</template>

<script lang="ts">
/**
 * Special component for Romanza default section.
 *
 * @author Jasper Howard <jhoward@peppermillcas.com>
 * @version 2.0.0
 * @copyright  Copyright (C) 2021-2025, Peppermill Inc. All rights reserved. Redistribution and use in source and binary forms, with or without modification, are not permitted.
 *
 *    2021-12-23 (1.0.0) (jhoward)
 *      - Default layout for Romanza menu section.
 *
 *    2022-03-04 (1.0.1) (jhoward)
 *      - Fixed tag title, they are now dynamic.
 *
 *    2022-03-10 (1.1.0) (jhoward)
 *      - Refactor tags to use <menu-tag /> component.
 *      - Add <menu-tag-legend />.
 *
 *    2022-03-31 (1.1.1) (jhoward)
 *      - Add menu version number to Romanza_defaultSection.vue.
 *
 *    2022-04-21 (1.2.0) (jhoward)
 *      - Add menu item grouping to Romanza_defaultSection.vue.
 *      - Compute this.items in Romanza_defaultSection.vue.
 *
 *    2023-04-17 (1.2.0) (jhoward)
 *      - Refactor <Romanza_defaultSection> to use TypeScript.
 *      - <Romanza_defaultSection> section prop is required.
 *      - Refactor <Romanza_defaultSection> to use helper functions for computed properties.
 *
 *    2023-08-23 (1.3.0) (jhoward)
 *      - Use <menu-version> in <Romanza_defaultSection>.
 *
 *    2023-11-08 (1.3.1) (jhoward)
 *      - Move <menu-tag> to the left of titles in <Romanza_defaultSection>.
 *      - Allow HTML in item.mi_description in <Romanza_defaultSection>.
 *
 *    2024-07-19 (1.3.2) (jhoward)
 *      - Rename .visual_menu--tag CSS rules to .menu--tag in <romanza-default-section>.
 *
 *    2025-02-10 (1.4.0) (jhoward)
 *      - Add <menu-schedules> to the <Romanza_defaultSection> component if in admin view.
 *
 *  2025-04-08 (2.0.0) (jhoward)
 *    - Use <default-textual-section> and <default-visual-section> in <Romanza_defaultSection>.
 */

import {defineComponent, PropType} from "vue";
import {ALLOWED_ITEM_COMPONENTS, Menu, MENU_ITEM_TAG_LOCATIONS, MenuSection} from "../defs";
import DefaultVisualSection from "./visual/defaultVisualSection.vue";
import DefaultTextualSection from "./textual/defaultTextualSection.vue";
import MenuItemGroup from "../menu-item-group.vue";

export default defineComponent({
  components: {
    MenuItemGroup,
    DefaultTextualSection, DefaultVisualSection,
  },
  name: "Romanza_defaultSection",
  props: {
    section: {
      type: Object as PropType<MenuSection>,
      required: true
    },
    menu: {
      type: Object as PropType<Menu>,
      required: true
    },
    menuItemComponent: {
      type: String,
      default: '',
      validate: (value: string) => '' === value || ALLOWED_ITEM_COMPONENTS.indexOf(value)
    },
    menuItemTagLocation: {
      type: String as PropType<MENU_ITEM_TAG_LOCATIONS>,
      default: 'before-title'
    },
  },
})
</script>

<style scoped>
.menu--section-romanza:deep(.menu--item) {
  text-align: center;
}

.menu--section-romanza:not(.menu--section-show_pricing_header):deep(.menu--item_price) {
  margin-left: 1em;
}

.menu--section-romanza:deep(.menu--item_description) {
  margin-top: 0;
  font-style: italic;
  font-size: 0.9em;
}

.menu--section-romanza:not(.menu--section-wine_list):deep(h3) {
  font-size: 1.2em;
  font-weight: bold;
}

.menu--section-romanza:deep(.visual_menu--page_section-top),
.menu--section-romanza:deep(.visual_menu--page_section-bottom) {
  text-align: center;
  padding-bottom: 1.5em;
  margin-top: -1.2em;
}




.menu--section-romanza:not(.menu--section-romanza_dessert):not(.menu--section-wine_list):deep(.menu--section_subgroup_title) {
  text-align: center;
  color: #c15e21;
  font-size: 1.05em;
}

.menu--section-romanza:deep(.menu--section_group_title:not(:first-child)),
.menu--section-romanza:deep(.menu--item_subgroup:not(:first-child) .menu--section_subgroup_title) {
  margin-top: 1em;
}





.menu--section-romanza:deep(.menu--item_title .menu--tag) {
  margin-top: -0.5em;
  margin-right: 0.3em;
  position: relative;
  right: initial;
}



/*START SECTION TITLES*/
.menu--section-romanza:deep(.menu--section_title) {
  font-size: 3.3em;
  margin-top: 0;
  color: #c15e21;
  display: grid;
  grid-template-columns: 1fr max-content 1fr;
  grid-gap: 0.2em;
  font-family: italian-breakfast, sans-serif;
  margin-left: -3em;
  width: calc(100% + 6em);
  max-width: initial;
  padding-left: 0;
}

.menu--section-romanza:deep(.menu--section_title .visual_menu--title_hyphen) {
  font-family: spumante, sans-serif;
}

.menu--section-romanza:deep(.menu--section_title > span:first-child),
.menu--section-romanza:deep(.menu--section_title > span:last-child) {
  position: relative;
}

.menu--section-romanza:deep(.menu--section_title > span:first-child::before),
.menu--section-romanza:deep(.menu--section_title > span:last-child::before) {
  content: '';
  position: absolute;
  top: 50%;
  left: 0;
  width: 100%;
  height: 1px;
  margin-top: 0.1em;
  background-color: #c15e21;
}

.menu--section-romanza:deep(.menu--section_title > span:last-child) {
  opacity: 0;
}
/*END SECTION TITLES*/
</style>